"use client";

import { createContext, useCallback, useContext, useRef, type ReactNode } from "react";
import Button from "@/components/ds/button";
import { stagger } from "@/components/motion/stagger";
import "./video-player.css";

const PLAY =
  "M39.3652 36.2746L50.9056 42.3047C51.239 42.4789 51.5153 42.7269 51.7072 43.0242C51.8991 43.3215 52 43.6578 52 44C52 44.3422 51.8991 44.6785 51.7072 44.9758C51.5153 45.2731 51.239 45.5211 50.9056 45.6953L39.3652 51.7254C39.0265 51.9024 38.6408 51.9972 38.2473 51.9999C37.8537 52.0027 37.4663 51.9134 37.1245 51.741C36.7827 51.5687 36.4986 51.3195 36.3011 51.0188C36.1036 50.718 35.9997 50.3764 36 50.0287V37.9713C35.9997 37.6236 36.1036 37.282 36.3011 36.9812C36.4986 36.6805 36.7827 36.4313 37.1245 36.259C37.4663 36.0866 37.8537 35.9973 38.2473 36.0001C38.6408 36.0028 39.0265 36.0975 39.3652 36.2746Z";

/** watch-video-button.svg, inlined without its foreignObject blur (an <img> never painted it). */
function VideoSquare() {
  return (
    <svg className="v2-video__svg" width="88" height="88" viewBox="0 0 88 88" fill="none" overflow="visible" aria-hidden="true">
      <defs>
        <filter id="v2-video-shadow" x="0" y="0" width="88" height="88" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="6" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.16 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <filter id="v2-video-inner" x="35.5" y="35.5" width="17" height="18" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.16 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
        </filter>
        <linearGradient id="v2-video-fill" x1="85.5" y1="6.5" x2="-14.5" y2="110.5" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="v2-video-edge" x1="74" y1="11" x2="15" y2="77.5" gradientUnits="userSpaceOnUse">
          <stop offset="0.0689432" stopColor="white" />
          <stop offset="0.786132" stopColor="white" stopOpacity="0" />
          <stop offset="0.976007" stopColor="white" />
        </linearGradient>
        <linearGradient id="v2-video-play-edge" x1="55.5" y1="36" x2="36.2749" y2="59.5944" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <g filter="url(#v2-video-shadow)">
        <rect className="v2-video__fill" x="12" y="11" width="64" height="64" rx="3" fill="url(#v2-video-fill)" fillOpacity="0.1" shapeRendering="crispEdges" />
        <rect className="v2-video__edge" x="12.5" y="11.5" width="63" height="63" rx="2.5" stroke="url(#v2-video-edge)" shapeRendering="crispEdges" pathLength={1} />
      </g>
      <g className="v2-video__play">
        <g filter="url(#v2-video-inner)">
          <path d={PLAY} fill="white" fillOpacity="0.2" />
        </g>
        <path d={PLAY} stroke="url(#v2-video-play-edge)" strokeLinecap="round" strokeLinejoin="round" />
        {/* Solid white on hover (hero.css): faded in over the glass triangle. */}
        <path className="v2-video__play-solid" d={PLAY} fill="white" />
      </g>
    </svg>
  );
}

const LABEL = "Watch the video";
const SOURCES = "/assets/video/motion-v3";

/* The drawn square inside the 88 x 88 drawing (rect x 12, y 11, 64 x 64): what the video grows out of, and shrinks
   back into. Kept next to VideoSquare so the two cannot drift apart. */
const SQUARE = { x: 12, y: 11, size: 64 };

/** How long the frame takes to reach full screen, and to fold back. Mirrors --v2-dur-base (tokens.css, 320 ms);
    only used as the safety delay if the animation never reports its end (see requestClose). */
const FOLD_MS = 320;

/**
 * "Watch the video": the hero's square opens the film full screen.
 *
 * Native `<dialog>`.showModal() carries most of what a full-screen player owes a keyboard: Escape closes, focus is
 * trapped inside, focus comes back to the button on close, the rest of the page is inert and hidden from screen
 * readers, and the backdrop is the browser's own. What is added here: the page behind is kept from scrolling, the
 * film is paused and rewound on close, and the frame grows out of the square (transform + opacity only).
 *
 * Native `controls` rather than a hand-made bar: it is what the visitor expects, it is keyboard- and
 * screen-reader-ready for free, and Chrome hides it after a few seconds of play, which leaves the minimal picture
 * asked for. Our own chrome is the single Close button.
 *
 * The film starts with sound. The click on the button is a user gesture, so the autoplay policy allows it; should a
 * browser refuse anyway, play() rejects, nothing is muted behind the visitor's back, and the native controls are
 * already on screen for them to start it.
 *
 * Without JavaScript the button is hidden (hero.css, `@media not (scripting: enabled)`) rather than offering
 * something it cannot do.
 *
 * One dialog, two triggers: the square (VideoSquareButton, wide screens) and, on phones where the square has no room,
 * a third call to action under the other two (VideoButton, hero.css). The player wraps the hero card and hands its
 * `open` down through a context, so the dialog is never duplicated and each trigger passes itself as the opener: the
 * frame grows out of it, and the focus comes back to it.
 */
type Opener = (opener: HTMLElement) => void;
const OpenVideo = createContext<Opener>(() => {});

export default function VideoPlayer({ children }: { children: ReactNode }) {
  const openerRef = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const closingRef = useRef(false);

  const open = useCallback<Opener>((opener) => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    openerRef.current = opener;

    closingRef.current = false;
    dialog.removeAttribute("data-closing");

    // Keep the page behind still, and give back the width the scrollbar was holding so nothing shifts.
    const root = document.documentElement;
    root.style.setProperty("--v2-vp-gutter", `${window.innerWidth - root.clientWidth}px`);
    root.classList.add("v2-vp-locked");

    dialog.showModal();

    // The square the film comes out of, and the box it lands in: one transform maps the second onto the first.
    // The frame is already animating by now, so its own bounding box is the SCALED one — reading it would measure
    // the animation instead of the layout, and the first version of this did exactly that (scale came out at 0.83
    // instead of 0.05). So: offsetWidth/Height for the resting size, and the dialog (which never animates) for the
    // centre the frame is centred in.
    // The square: its drawn square inside the 88 x 88 drawing. Any other trigger (the phone button): its own box.
    const box = opener.getBoundingClientRect();
    const from = opener.classList.contains("v2-hero__video")
      ? { cx: box.x + SQUARE.x + SQUARE.size / 2, cy: box.y + SQUARE.y + SQUARE.size / 2, size: SQUARE.size }
      : { cx: box.x + box.width / 2, cy: box.y + box.height / 2, size: box.height };
    const frameEl = frameRef.current;
    const stage = dialog.getBoundingClientRect();
    if (frameEl && frameEl.offsetWidth > 0) {
      const frame = {
        width: frameEl.offsetWidth,
        height: frameEl.offsetHeight,
        x: stage.x + (stage.width - frameEl.offsetWidth) / 2,
        y: stage.y + (stage.height - frameEl.offsetHeight) / 2,
      };
      const scale = from.size / frame.width;
      const x = from.cx - (frame.x + frame.width / 2);
      const y = from.cy - (frame.y + frame.height / 2);
      dialog.style.setProperty("--v2-vp-x", `${Math.round(x)}px`);
      dialog.style.setProperty("--v2-vp-y", `${Math.round(y)}px`);
      dialog.style.setProperty("--v2-vp-scale", `${scale}`);
    }

    closeRef.current?.focus();

    const video = videoRef.current;
    if (video) {
      video.muted = false;
      // Rejected (a browser that refuses sound without its own gesture): the native controls are already there.
      void video.play().catch(() => {});
    }
  }, []);

  const finishClose = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog?.open) return;
    dialog.removeAttribute("data-closing");
    closingRef.current = false;
    dialog.close(); // onClose gives the focus back to whichever trigger opened it
  }, []);

  const requestClose = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog?.open || closingRef.current) return;

    // The sound stops now, not at the end of the fold; and the film starts over next time.
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      finishClose();
      return;
    }

    closingRef.current = true;
    dialog.setAttribute("data-closing", "");
    // animationend does the closing; this only keeps a failed animation from trapping the visitor.
    window.setTimeout(finishClose, FOLD_MS + 200);
  }, [finishClose]);

  return (
    <OpenVideo.Provider value={open}>
      {children}

      <dialog
        ref={dialogRef}
        className="v2-vp"
        aria-label={LABEL}
        onCancel={(event) => {
          // Escape: fold the frame back into the square instead of blinking out.
          event.preventDefault();
          requestClose();
        }}
        onClose={() => {
          document.documentElement.classList.remove("v2-vp-locked");
          openerRef.current?.focus();
        }}
        onClick={(event) => {
          if (event.target === dialogRef.current) requestClose();
        }}
      >
        <div
          ref={frameRef}
          className="v2-vp__frame"
          onAnimationEnd={(event) => {
            if (event.animationName === "v2-vp-rise" && closingRef.current) finishClose();
          }}
        >
          {/* preload="none": nothing of the 7 MB leaves the server before the click. */}
          <video ref={videoRef} className="v2-vp__video" controls playsInline preload="none" poster={`${SOURCES}-poster.jpg`}>
            <source src={`${SOURCES}.webm`} type="video/webm" />
            <source src={`${SOURCES}.mp4`} type="video/mp4" />
          </video>
        </div>

        <button ref={closeRef} type="button" className="v2-vp__close" onClick={requestClose} aria-label="Close the video">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M5 5 15 15M15 5 5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </dialog>
    </OpenVideo.Provider>
  );
}

/** The square beside the headline (wide screens; hidden at 720 px and below, hero.css). */
export function VideoSquareButton() {
  const open = useContext(OpenVideo);
  return (
    <button type="button" className="v2-hero__video" onClick={(event) => open(event.currentTarget)}>
      <VideoSquare />
      {/* Hover: a light runs round the square's edge, like the glass button's (hero.css). */}
      <span className="v2-hero__video-ring" />
      <span className="v2-hero__video-label">
        {[...LABEL].map((char, i) => (
          <span key={i} style={stagger(i)}>
            {char}
          </span>
        ))}
      </span>
    </button>
  );
}

/** The phone's trigger: a third call to action under Book a call and Why Claude (shown at 720 px and below only). */
export function VideoButton() {
  const open = useContext(OpenVideo);
  return (
    <Button variant="glass" icon="play" className="v2-hero__video-cta" onClick={(event) => open(event.currentTarget)}>
      {LABEL}
    </Button>
  );
}
